/**
 * AdminVouchersPromosPage.jsx
 * ============================
 * Sprint R4C — Diploks Vouchers & Promos console (super_admin only).
 *
 * Tabs:
 *   1. Gift Vouchers     — fixed-credit voucher campaigns + bulk codes
 *   2. Top-Up Promo Codes — Bonus Diploks on paid top-ups
 *   3. Advertisement Campaigns — promo campaigns with banner content
 *   4. Redemptions       — voucher + promo redemption logs
 *   5. Analytics         — KPIs
 */
import { useCallback, useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Gift, Megaphone, Receipt, BarChart3, PlusCircle, RefreshCw, X,
  CheckCircle2, Power, Copy,
} from "lucide-react";

const TABS = [
  { key: "vouchers", label: "Gift Vouchers", icon: Gift },
  { key: "promos", label: "Top-Up Promos", icon: PlusCircle },
  { key: "ads", label: "Advertisements", icon: Megaphone },
  { key: "redemptions", label: "Redemptions", icon: Receipt },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
];

const PROMO_TYPE_LABELS = {
  bonus_topup: "Fixed Bonus",
  percentage_bonus_topup: "% Bonus",
  event_advertisement: "Event Ad",
  organisation_advertisement: "Organisation Ad",
  sponsor_funded: "Sponsor-funded",
  fixed_gift_voucher: "Gift voucher",
};

export default function AdminVouchersPromosPage() {
  const [tab, setTab] = useState("vouchers");
  const [vouchers, setVouchers] = useState([]);
  const [promos, setPromos] = useState([]);
  const [redemptions, setRedemptions] = useState({ vouchers: [], promos: [] });
  const [analytics, setAnalytics] = useState({ vouchers: null, promos: null });
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [v, p, va, pa] = await Promise.all([
        api.get("/admin/diploks/vouchers?limit=200"),
        api.get("/admin/diploks/promos?limit=200"),
        api.get("/admin/diploks/vouchers/analytics?days=30"),
        api.get("/admin/diploks/promos/analytics?days=30"),
      ]);
      setVouchers(v.data?.campaigns || []);
      setPromos(p.data?.campaigns || []);
      setAnalytics({ vouchers: va.data, promos: pa.data });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const toggleVoucher = async (id, currentActive) => {
    try {
      await api.post(`/admin/diploks/vouchers/${id}/${currentActive ? "deactivate" : "activate"}`);
      toast.success(`Voucher ${currentActive ? "deactivated" : "activated"}.`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePromo = async (id, currentActive) => {
    try {
      await api.post(`/admin/diploks/promos/${id}/${currentActive ? "deactivate" : "activate"}`);
      toast.success(`Promo ${currentActive ? "deactivated" : "activated"}.`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout title="Diploks Vouchers & Promos">
      <div data-testid="vouchers-promos-page" className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-navy-100 bg-white px-5 py-4 shadow-sm">
          <div>
            <h1 className="text-xl font-semibold text-navy-900">Vouchers &amp; Promos</h1>
            <p className="mt-1 text-sm text-slate-600">
              Growth engine for Diploks — fixed gift vouchers, bonus
              top-up promos and advertisement campaigns.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="vp-refresh"
              onClick={refresh}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw size={12} /> Refresh
            </button>
            <button
              data-testid="vp-new-voucher"
              onClick={() => setShowVoucherModal(true)}
              className="inline-flex items-center gap-1 rounded-md bg-violet-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-800"
            >
              <Gift size={12} /> New voucher
            </button>
            <button
              data-testid="vp-new-promo"
              onClick={() => setShowPromoModal(true)}
              className="inline-flex items-center gap-1 rounded-md bg-amber-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-800"
            >
              <PlusCircle size={12} /> New promo
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`vp-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  tab === t.key
                    ? "border-navy-700 bg-navy-900 text-white shadow"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                }`}
              >
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Voucher campaigns */}
        {tab === "vouchers" ? (
          <CampaignTable
            testid="vp-voucher-table"
            rows={vouchers}
            type="voucher"
            onToggle={toggleVoucher}
          />
        ) : null}

        {/* Top-up promos */}
        {tab === "promos" ? (
          <CampaignTable
            testid="vp-promo-table"
            rows={promos.filter((p) =>
              ["bonus_topup", "percentage_bonus_topup", "sponsor_funded"]
                .includes(p.campaign_type))}
            type="promo"
            onToggle={togglePromo}
          />
        ) : null}

        {/* Advertisement campaigns */}
        {tab === "ads" ? (
          <CampaignTable
            testid="vp-ads-table"
            rows={promos.filter((p) => p.advertisement_title)}
            type="ad"
            onToggle={togglePromo}
          />
        ) : null}

        {/* Redemptions */}
        {tab === "redemptions" ? (
          <RedemptionsPanel />
        ) : null}

        {/* Analytics */}
        {tab === "analytics" ? (
          <AnalyticsPanel analytics={analytics} />
        ) : null}

        <NewVoucherModal
          open={showVoucherModal}
          onClose={() => setShowVoucherModal(false)}
          onCreated={(out) => { setGeneratedCodes(out); setShowVoucherModal(false); refresh(); }}
        />
        <NewPromoModal
          open={showPromoModal}
          onClose={() => setShowPromoModal(false)}
          onCreated={() => { setShowPromoModal(false); refresh(); }}
        />
        <GeneratedCodesModal
          out={generatedCodes}
          onClose={() => setGeneratedCodes(null)}
        />
      </div>
    </DashboardLayout>
  );
}

function CampaignTable({ rows, type, onToggle, testid }) {
  return (
    <div data-testid={testid} className="overflow-hidden rounded-2xl border border-navy-100 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Type</th>
              <th className="px-4 py-2 text-left">Value</th>
              <th className="px-4 py-2 text-left">Code</th>
              <th className="px-4 py-2 text-left">Active</th>
              <th className="px-4 py-2 text-left">Expiry</th>
              <th className="px-4 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">No campaigns yet.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} data-testid={`vp-row-${r.id}`}>
                  <td className="px-4 py-2">
                    <div className="font-medium text-navy-900">{r.name || r.campaign_name}</div>
                    {r.advertisement_title ? (
                      <div className="text-[11px] text-amber-700">{r.advertisement_title}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {type === "voucher" ? "Gift voucher" : (PROMO_TYPE_LABELS[r.campaign_type] || r.campaign_type)}
                  </td>
                  <td className="px-4 py-2 text-xs font-semibold">
                    {type === "voucher"
                      ? `${r.diploks_value} Diploks${r.quantity ? ` × ${r.quantity}` : ""}`
                      : `${r.bonus_type === "percentage" ? `${r.bonus_value}%` : `${r.bonus_value} Diploks`}`}
                  </td>
                  <td className="px-4 py-2 text-xs font-mono">
                    {r.promo_code_mask || (r.single_reusable_code ? "Reusable" : `${r.quantity || 0} codes`)}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      r.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                    }`}>
                      {r.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {r.expiry_date ? r.expiry_date.slice(0, 10) : "—"}
                  </td>
                  <td className="px-4 py-2 text-xs text-right">
                    <button
                      data-testid={`vp-toggle-${r.id}`}
                      onClick={() => onToggle(r.id, r.active)}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] font-medium hover:bg-slate-50"
                    >
                      <Power size={10} />
                      {r.active ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RedemptionsPanel() {
  const [voucherReds, setVoucherReds] = useState([]);
  const [promoReds, setPromoReds] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [v, p] = await Promise.all([
          api.get("/admin/diploks/vouchers?limit=1"),
          api.get("/admin/diploks/promos?limit=1"),
        ]);
        // Aggregate redemptions across recent campaigns
        const vCampaigns = v.data?.campaigns || [];
        const pCampaigns = p.data?.campaigns || [];
        const vAll = [];
        for (const c of vCampaigns.slice(0, 5)) {
          try {
            const r = await api.get(`/admin/diploks/vouchers/${c.id}/redemptions?limit=20`);
            vAll.push(...(r.data?.redemptions || []));
          } catch { /* one campaign's redemptions failed — keep going */ }
        }
        setVoucherReds(vAll.slice(0, 50));
        // Promos: not exposing per-id redemptions endpoint here, use analytics
        setPromoReds(pCampaigns.slice(0, 0)); // empty for now
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);
  return (
    <div data-testid="vp-redemptions-panel" className="space-y-4">
      <div className="overflow-hidden rounded-2xl border border-navy-100 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 text-sm font-medium text-slate-700">
          Recent voucher redemptions ({voucherReds.length})
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">When</th>
                <th className="px-4 py-2 text-left">Campaign</th>
                <th className="px-4 py-2 text-left">User</th>
                <th className="px-4 py-2 text-right">Diploks</th>
                <th className="px-4 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {voucherReds.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">No redemptions yet.</td></tr>
              ) : (
                voucherReds.map((r) => (
                  <tr key={r.id} data-testid={`vp-vred-row-${r.id}`}>
                    <td className="px-4 py-2 font-mono text-xs text-slate-600">{r.redeemed_at?.slice(0,19).replace("T"," ")}</td>
                    <td className="px-4 py-2 font-mono text-[10px]">{r.campaign_id}</td>
                    <td className="px-4 py-2 font-mono text-[10px]">{r.user_id}</td>
                    <td className="px-4 py-2 text-right font-semibold text-emerald-700">+{r.diploks_credited}</td>
                    <td className="px-4 py-2 text-xs">{r.redemption_status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AnalyticsPanel({ analytics }) {
  const v = analytics?.vouchers || {};
  const p = analytics?.promos || {};
  return (
    <div data-testid="vp-analytics-panel" className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-navy-900">Vouchers (last 30d)</h3>
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-5">
          <Kpi testid="vp-v-active" label="Active campaigns" value={v.active_campaigns} />
          <Kpi testid="vp-v-codes" label="Codes" value={v.total_codes} hint={`${v.used_codes ?? 0} redeemed`} />
          <Kpi testid="vp-v-redemptions" label="Redemptions" value={v.total_redemptions} hint={`${v.recent_redemptions ?? 0} in 30d`} />
          <Kpi testid="vp-v-diploks" label="Diploks issued" value={v.total_diploks_issued_via_vouchers} />
          <Kpi testid="vp-v-expired" label="Expired campaigns" value={v.expired_campaigns} />
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-navy-900">Promos (last 30d)</h3>
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-5">
          <Kpi testid="vp-p-active" label="Active campaigns" value={p.active_campaigns} />
          <Kpi testid="vp-p-redemptions" label="Redemptions" value={p.total_redemptions} hint={`${p.recent_redemptions ?? 0} in 30d`} />
          <Kpi testid="vp-p-bonus" label="Bonus Diploks issued" value={p.total_bonus_diploks_issued} />
          <Kpi testid="vp-p-purchased" label="Linked Diploks purchased" value={p.total_purchased_diploks_via_promos} />
          <Kpi testid="vp-p-expired" label="Expired campaigns" value={p.expired_campaigns} />
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, hint, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-navy-100 bg-white px-4 py-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-navy-900">{value ?? "—"}</div>
      {hint ? <div className="mt-0.5 text-[10px] text-slate-500">{hint}</div> : null}
    </div>
  );
}

function NewVoucherModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", diploks_value: 50, quantity: 10, code_prefix: "DIP",
    single_reusable_code: false, expiry_iso: "",
    per_user_limit: 1, per_wallet_limit: 1,
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setForm({ name: "", diploks_value: 50, quantity: 10, code_prefix: "DIP", single_reusable_code: false, expiry_iso: "", per_user_limit: 1, per_wallet_limit: 1 }); }, [open]);
  if (!open) return null;
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    if (form.diploks_value < 1) { toast.error("Diploks value must be ≥ 1."); return; }
    setBusy(true);
    try {
      const body = {
        ...form,
        expiry_iso: form.expiry_iso ? new Date(form.expiry_iso).toISOString() : null,
      };
      const r = await api.post("/admin/diploks/vouchers/create", body);
      toast.success(`Voucher campaign created — ${r.data?.codes?.length || 0} codes generated.`);
      onCreated(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="New voucher campaign" onClose={onClose} testid="vp-voucher-modal">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Campaign name" testid="vp-v-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <Field label="Diploks per voucher" type="number" testid="vp-v-value" value={form.diploks_value} onChange={(v) => setForm({ ...form, diploks_value: Number(v) })} />
        <Field label="Quantity (codes)" type="number" testid="vp-v-qty" value={form.quantity} onChange={(v) => setForm({ ...form, quantity: Number(v) })} />
        <Field label="Code prefix" testid="vp-v-prefix" value={form.code_prefix} onChange={(v) => setForm({ ...form, code_prefix: v })} />
        <Field label="Expiry (ISO)" type="datetime-local" testid="vp-v-expiry" value={form.expiry_iso} onChange={(v) => setForm({ ...form, expiry_iso: v })} />
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            data-testid="vp-v-single"
            checked={form.single_reusable_code}
            onChange={(e) => setForm({ ...form, single_reusable_code: e.target.checked, quantity: e.target.checked ? 1 : form.quantity })}
          />
          Single reusable code (e.g. MUNWELCOME50)
        </label>
      </div>
      <div className="mt-5 flex justify-end gap-3">
        <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
        <button
          data-testid="vp-v-submit"
          disabled={busy || !form.name.trim() || form.diploks_value < 1}
          onClick={submit}
          className="rounded-lg bg-violet-700 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-800 disabled:bg-violet-300"
        >
          {busy ? "Creating…" : "Create + generate codes"}
        </button>
      </div>
    </Modal>
  );
}

function NewPromoModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    campaign_name: "", promo_code: "",
    campaign_type: "bonus_topup", bonus_type: "fixed", bonus_value: 25,
    eligible_currencies: "", min_topup_amount: "",
    advertisement_title: "", advertisement_subtitle: "",
    advertisement_cta: "Apply Code", expiry_date: "",
    per_user_limit: 1, per_wallet_limit: 1, max_redemptions: "",
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setForm({ campaign_name: "", promo_code: "", campaign_type: "bonus_topup", bonus_type: "fixed", bonus_value: 25, eligible_currencies: "", min_topup_amount: "", advertisement_title: "", advertisement_subtitle: "", advertisement_cta: "Apply Code", expiry_date: "", per_user_limit: 1, per_wallet_limit: 1, max_redemptions: "" }); }, [open]);
  if (!open) return null;
  const submit = async () => {
    if (!form.campaign_name.trim()) { toast.error("Campaign name is required."); return; }
    if (!form.promo_code.trim()) { toast.error("Promo code is required."); return; }
    setBusy(true);
    try {
      const body = {
        campaign_name: form.campaign_name, promo_code: form.promo_code,
        campaign_type: form.campaign_type, bonus_type: form.bonus_type,
        bonus_value: Number(form.bonus_value),
        eligible_currencies: form.eligible_currencies ? form.eligible_currencies.split(",").map(s => s.trim()).filter(Boolean) : undefined,
        min_topup_amount: form.min_topup_amount ? Number(form.min_topup_amount) : undefined,
        advertisement_title: form.advertisement_title || undefined,
        advertisement_subtitle: form.advertisement_subtitle || undefined,
        advertisement_cta: form.advertisement_cta || undefined,
        expiry_date: form.expiry_date ? new Date(form.expiry_date).toISOString() : undefined,
        per_user_limit: Number(form.per_user_limit),
        per_wallet_limit: Number(form.per_wallet_limit),
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : undefined,
      };
      await api.post("/admin/diploks/promos/create", body);
      toast.success("Promo campaign created.");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="New top-up promo campaign" onClose={onClose} testid="vp-promo-modal">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Campaign name" testid="vp-p-name" value={form.campaign_name} onChange={(v) => setForm({ ...form, campaign_name: v })} />
        <Field label="Promo code" testid="vp-p-code" value={form.promo_code} onChange={(v) => setForm({ ...form, promo_code: v.toUpperCase() })} />
        <Select label="Campaign type" testid="vp-p-type" value={form.campaign_type} options={[
          ["bonus_topup", "Fixed bonus"],
          ["percentage_bonus_topup", "Percentage bonus"],
          ["event_advertisement", "Event advertisement"],
          ["organisation_advertisement", "Organisation advertisement"],
          ["sponsor_funded", "Sponsor-funded"],
        ]} onChange={(v) => setForm({ ...form, campaign_type: v })} />
        <Select label="Bonus type" testid="vp-p-bonus-type" value={form.bonus_type} options={[
          ["fixed", "Fixed Diploks"],
          ["percentage", "Percentage of purchase"],
        ]} onChange={(v) => setForm({ ...form, bonus_type: v })} />
        <Field label={form.bonus_type === "percentage" ? "Bonus %" : "Bonus Diploks"} type="number" testid="vp-p-bonus-value" value={form.bonus_value} onChange={(v) => setForm({ ...form, bonus_value: v })} />
        <Field label="Min top-up amount (currency unit)" type="number" testid="vp-p-min" value={form.min_topup_amount} onChange={(v) => setForm({ ...form, min_topup_amount: v })} />
        <Field label="Eligible currencies (comma sep, e.g. INR,USD)" testid="vp-p-cur" value={form.eligible_currencies} onChange={(v) => setForm({ ...form, eligible_currencies: v })} />
        <Field label="Per-user limit" type="number" testid="vp-p-per-user" value={form.per_user_limit} onChange={(v) => setForm({ ...form, per_user_limit: Number(v) })} />
        <Field label="Max total redemptions" type="number" testid="vp-p-max-red" value={form.max_redemptions} onChange={(v) => setForm({ ...form, max_redemptions: v })} />
        <Field label="Expiry" type="datetime-local" testid="vp-p-expiry" value={form.expiry_date} onChange={(v) => setForm({ ...form, expiry_date: v })} />
        <Field label="Advertisement title" testid="vp-p-ad-title" value={form.advertisement_title} onChange={(v) => setForm({ ...form, advertisement_title: v })} />
        <Field label="Advertisement subtitle" testid="vp-p-ad-subtitle" value={form.advertisement_subtitle} onChange={(v) => setForm({ ...form, advertisement_subtitle: v })} />
        <Field label="CTA label" testid="vp-p-cta" value={form.advertisement_cta} onChange={(v) => setForm({ ...form, advertisement_cta: v })} />
      </div>
      <div className="mt-5 flex justify-end gap-3">
        <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
        <button
          data-testid="vp-p-submit"
          disabled={busy || !form.campaign_name.trim() || !form.promo_code.trim()}
          onClick={submit}
          className="rounded-lg bg-amber-700 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-800 disabled:bg-amber-300"
        >
          {busy ? "Creating…" : "Create promo"}
        </button>
      </div>
    </Modal>
  );
}

function GeneratedCodesModal({ out, onClose }) {
  // useEffect already imported at top
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (out) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [out, onClose]);
  if (!out) return null;
  const codes = out.codes || [];
  const copyAll = () => {
    const text = codes.map((c) => c.code_plain).join("\n");
    navigator.clipboard.writeText(text);
    toast.success("All codes copied to clipboard.");
  };
  return (
    <Modal title="Generated voucher codes — copy now (only shown once)" onClose={onClose} testid="vp-generated-modal">
      <p className="text-xs text-rose-700 mb-3">
        These plain codes will NOT be shown again. Copy them now.
      </p>
      <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-xs">
        {codes.map((c) => (
          <div key={c.id} className="py-0.5">{c.code_plain}</div>
        ))}
      </div>
      <div className="mt-3 flex justify-end gap-3">
        <button onClick={copyAll} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
          <Copy size={12} /> Copy all
        </button>
        <button onClick={onClose} className="rounded-md bg-navy-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy-800">
          Done
        </button>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose, testid }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div data-testid={testid} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-lg font-semibold text-navy-900">{title}</h3>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block text-xs">
      <span className="font-medium text-slate-600">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
      />
    </label>
  );
}

function Select({ label, value, options, onChange, testid }) {
  return (
    <label className="block text-xs">
      <span className="font-medium text-slate-600">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
      >
        {options.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    </label>
  );
}
